import { useEffect, useRef, useState } from 'react'

/**
 * Enseña la navegación 2D la primera vez.
 *
 * La carta se mueve en dos ejes —horizontal entre platos, vertical entre
 * secciones— y eso no lo adivina nadie. Sin esto, el comensal desliza a los
 * lados, recorre una sección y cree que ha visto la carta entera.
 *
 * Se justifica por ENSEÑAR, no por tapar una descarga. Es una distinción con
 * consecuencias: cuando más adelante haya vídeo, esta pantalla será además la
 * ventana natural para precargar el primero, pero si su razón de ser fuera esa,
 * el día que la descarga se optimizara habría que quitarla y se perdería lo
 * único que de verdad aporta.
 *
 * Reglas que la mantienen del lado del comensal:
 *  · aparece UNA vez, y al entrar al escenario, no al abrir la carta —quien
 *    scanea un QR con hambre quiere ver comida, no un tutorial—;
 *  · se cierra con un toque en cualquier sitio, con Escape o con el botón;
 *  · si el comensal ya sabe moverse, el primer gesto la descarta sola.
 */

const SEEN_KEY = 'cartazo:gestos'

function alreadySeen(): boolean {
  try {
    return localStorage.getItem(SEEN_KEY) === '1'
  } catch {
    // Navegación privada con almacenamiento bloqueado: mejor enseñarlo de más
    // que dejar a alguien sin saber moverse.
    return false
  }
}

function remember(): void {
  try {
    localStorage.setItem(SEEN_KEY, '1')
  } catch {
    /* sin almacenamiento, se volverá a ver: es el fallo correcto */
  }
}

export function GestureHint({ active }: { active: boolean }) {
  const [open, setOpen] = useState(false)
  const closeRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    if (active && !alreadySeen()) setOpen(true)
  }, [active])

  useEffect(() => {
    if (!open) return

    const dismiss = () => {
      remember()
      setOpen(false)
    }

    const onKey = (event: KeyboardEvent) => {
      // Escape cierra; cualquier flecha significa "ya sé moverme" y también.
      if (event.key === 'Escape' || event.key.startsWith('Arrow')) dismiss()
    }

    document.addEventListener('keydown', onKey)
    // El foco va al botón para que quien navega con teclado no quede atrapado
    // detrás de una capa que no puede ver.
    closeRef.current?.focus()

    return () => document.removeEventListener('keydown', onKey)
  }, [open])

  if (!open) return null

  const close = () => {
    remember()
    setOpen(false)
  }

  return (
    <div
      className="gesturehint"
      role="dialog"
      aria-modal="true"
      aria-labelledby="gesturehint-title"
      // Un toque en cualquier parte cierra: es lo que hace la mayoría, y pelear
      // contra ello solo convierte la ayuda en un obstáculo.
      onPointerDown={close}
    >
      <div className="gesturehint__card">
        <h2 className="gesturehint__title" id="gesturehint-title">
          Cómo moverte
        </h2>

        <ul className="gesturehint__list">
          <li>
            <span className="gesturehint__glyph" aria-hidden="true">
              ←→
            </span>
            <span>
              Desliza <strong>a los lados</strong> para pasar de plato
            </span>
          </li>
          <li>
            <span className="gesturehint__glyph" aria-hidden="true">
              ↑↓
            </span>
            <span>
              Desliza <strong>arriba y abajo</strong> para cambiar de sección
            </span>
          </li>
          <li>
            <span className="gesturehint__glyph" aria-hidden="true">
              ☰
            </span>
            <span>
              Arriba a la derecha tienes el <strong>índice</strong> y la búsqueda
            </span>
          </li>
        </ul>

        <button ref={closeRef} type="button" className="button gesturehint__ok" onClick={close}>
          Entendido
        </button>
      </div>
    </div>
  )
}
